import { Box, Button, Flex, SimpleGrid, Stack, Text } from '@chakra-ui/react';
import type { MultiValue, SingleValue } from 'chakra-react-select';
import {
	type ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { AiOutlineSearch } from 'react-icons/ai';
import { IoEyeOutline } from 'react-icons/io5';
import { LuExpand } from 'react-icons/lu';
import { MdInfo } from 'react-icons/md';
import { TiWarning } from 'react-icons/ti';
import { BookmarkIcon, DocumentsIcon } from '@/assets/icons';
import FaceLiftSearchInput from '@/components/Atoms/FaceLiftSearchInput';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import FaceliftCheckbox from '@/components/Atoms/FaceliftCheckbox/FaceliftCheckbox';
import FaceliftDialog from '@/components/Atoms/FaceliftDialog';
import FaceliftInput from '@/components/Atoms/FaceliftInput';
import FaceliftMessage from '@/components/Atoms/FaceliftMessage';
import FaceliftSelect from '@/components/Atoms/FaceliftSelect/FaceliftSelect';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import FaceliftSwitch from '@/components/Atoms/FaceliftSwitch/FaceliftSwitch';
import FaceliftTabs from '@/components/Atoms/FaceliftTabs';
import type { DateRange } from '@/components/Molecules/FaceliftDatePicker';
import FaceliftDatePicker from '@/components/Molecules/FaceliftDatePicker';
import InvoiceAgingSummary from '@/components/Molecules/InvoiceAgingSummary';
import ScoreCard from '@/components/Molecules/ScoreCard';
import DataTable from '@/components/Organisms/DataTable';
import type {
	Column,
	ColumnData,
	onFilterApplyProps,
} from '@/components/Organisms/DataTable/DataTable.types';
import DonutChart from '@/components/Organisms/DonutChart';
import GradientBarChart from '@/components/Organisms/GradientBarChart';
import GroupedBarChart from '@/components/Organisms/GroupedBarChart';
import { InvoiceChart } from '@/components/Organisms/InvoiceChart';
import LineChart from '@/components/Organisms/LineChart';
import StackedBarChart from '@/components/Organisms/StackedBarChart/StackedBarChart';
import { Stepper, type StepperStep } from '@/components/Organisms/Stepper';
import Clinics from '@/components/Templates/Profile/Clinics';
import General from '@/components/Templates/Profile/General/General';
import Notifications from '@/components/Templates/Profile/Notifications';
import Products from '@/components/Templates/Profile/Products';
import { ColorModeButton } from '@/components/ui/color-mode';
import { colors } from '@/components/ui/theme';

type PaginatedItems = {
	id: number;
	name: string;
	category: string;
	price: string;
	bvStatus: ReactNode;
};
const Test = () => {
	const [search, setSearch] = useState('');
	const [checked, setChecked] = useState(false);
	const [open1, setOpen1] = useState(false);
	const [switchChecked, setSwitchChecked] = useState(false);
	const [tab, setTab] = useState('0');
	const [messages, setMessages] = useState({
		success: '',
		warning: '',
		info: '',
		error: '',
	});
	// Date picker states
	const [singleDate, setSingleDate] = useState('');
	const [dateRange, setDateRange] = useState<DateRange>({
		startDate: '',
		endDate: '',
	});

	const makeItems = useCallback(
		(count: number, start: number = 0): SelectItem[] => {
			return Array.from({ length: count }).map((_, i) => ({
				label: `Ele ${start + i + 1}`,
				value: `${start + i + 1}`,
			}));
		},
		[],
	);
	const [allItems, setAllItems] = useState([] as SelectItem[]);
	const [singleValue, setSingleValue] = useState<SingleValue<SelectItem>>(null);
	const [multiValue, setMultiValue] = useState<MultiValue<SelectItem>>([]);
	const onSingleChange = (newValue: SingleValue<SelectItem>) => {
		setSingleValue(newValue);
	};
	const onMultiChange = (newValue: MultiValue<SelectItem>) => {
		setMultiValue(newValue);
	};

	const [loading, setLoading] = useState(true);

	// const [filter, setFilter] = useState({ page: 1, limit: 10, search: '', status: 'all' });

	// const { data, isLoading, isError, isFetching } = useGetItems(filter);

	// const { mutate: addItem, isLoading } = useAddNewItem(onSuccess);

	// function onSuccess(data: any) {
	// navigate(`/items/`);
	// }

	// const handleSubmit = () => {
	// 	  addItem({
	// 			...formData
	//   });
	// }

	const onLoadMore = useCallback(
		(page: number, limit: number, search: string) => {
			console.log('data', page, limit, search);
			setLoading(true);
			setTimeout(() => {
				setAllItems((prev) => [...prev, ...makeItems(limit, prev.length)]);
				setLoading(false);
			}, 1000);
		},
		[makeItems],
	);

	useEffect(() => {
		setTimeout(() => {
			setLoading(false);
		}, 1000);
	}, []);

	// Define stepper steps
	const stepperSteps: StepperStep[] = [
		{
			name: 'Facility Information',
			element: (
				<Box>
					<Text>Step 1: Facility Information Content</Text>
					<Text>Step 1: Facility Information Content</Text>
					<Text>Step 1: Facility Information Content</Text>
					<Text>Step 1: Facility Information Content</Text>
					<Text>Step 1: Facility Information Content</Text>
					<Text>Step 1: Facility Information Content</Text>
					<Text>Step 1: Facility Information Content</Text>
				</Box>
			),
		},
		{
			name: 'Patient Information',
			element: <Text>Step 2: Patient Information Content</Text>,
		},
		{
			name: 'Insurance Details',
			element: <Text>Step 3: Insurance Details Content</Text>,
		},
		{
			name: 'Verification',
			element: <Text>Step 4: Verification Content</Text>,
		},
		{
			name: 'Review',
			element: <Text>Step 5: Review Content</Text>,
		},
	];

	// Chart Review form state
	const [chartReviewData, setChartReviewData] = useState<{
		clinic: MultiValue<SelectItem>;
		provider: MultiValue<SelectItem>;
		patient: MultiValue<SelectItem>;
		uploadDocuments: {
			uploadedFiles: Array<{
				id: string;
				name: string;
				size: number;
				type: string;
				file: File;
			}>;
		};
	}>({
		clinic: [],
		provider: [],
		patient: [],
		uploadDocuments: {
			uploadedFiles: [],
		},
	});

	const handleChartReviewChange = (
		field: string,
		value:
			| string
			| MultiValue<SelectItem>
			| {
				uploadedFiles: Array<{
					id: string;
					name: string;
					size: number;
					type: string;
					file: File;
				}>;
			},
	) => {
		setChartReviewData((prev) => ({ ...prev, [field]: value }));
	};
	const statusesFilterOptions = [
		{ label: 'Approved', value: 'approved' },
		{ label: 'Auth Approved', value: 'auth-approved' },
		{
			label: 'Appeal in Process',
			value: 'appeal-in-process',
		},
		{ label: 'In Process', value: 'in-process' },
		{
			label: 'Pending Clinic Response (Missing Data)',
			value: 'pending-clinic-response',
		},
		{ label: 'Void', value: 'void' },
		{ label: 'Denied', value: 'denied' },
		{ label: 'Auth Denied', value: 'auth-denied' },
		{ label: 'Order Placed', value: 'order-placed' },
		{ label: 'On Hold', value: 'on-hold' },
	];

	// ScoreCard data
	const scoreCardData = [
		{
			title: 'Benefit Verifications',
			value: 250,
			subtitle: 'This Month',
		},
		{
			title: 'Open Invoices',
			value: 100,
			subtitle: 'This Month',
		},
		{ title: 'Order Shipped', value: 35, subtitle: 'This Month' },
		{
			title: 'Order Returns',
			value: '05',
			subtitle: 'This Month',
		},
	];

	// Chart data
	const stackedChartData = [
		{
			category: 'JAN 2023',
			data: [
				{ title: 'Amino Tech', value: 150 },
				{ title: 'Imbed', value: 200 },
				{ title: 'Nvision Biomed', value: 150 },
				{ title: 'Axolotl Biologix', value: 100 },
			],
		},
		{
			category: 'FEB 2023',
			data: [
				{ title: 'Amino Tech', value: 150 },
				{ title: 'Imbed', value: 200 },
				{ title: 'Nvision Biomed', value: 150 },
				{ title: 'Axolotl Biologix', value: 100 },
			],
		},
		{
			category: 'MAR 2023',
			data: [
				{ title: 'Amino Tech', value: 250 },
				{ title: 'Imbed', value: 450 },
				{ title: 'Nvision Biomed', value: 200 },
				{ title: 'Axolotl Biologix', value: 350 },
			],
		},
		{
			category: 'APR 2023',
			data: [
				{ title: 'Amino Tech', value: 250 },
				{ title: 'Imbed', value: 450 },
				{ title: 'Nvision Biomed', value: 200 },
				{ title: 'Axolotl Biologix', value: 350 },
			],
		},
		{
			category: 'MAY 2023',
			data: [
				{ title: 'Amino Tech', value: 150 },
				{ title: 'Imbed', value: 200 },
				{ title: 'Nvision Biomed', value: 150 },
				{ title: 'Axolotl Biologix', value: 100 },
			],
		},
		{
			category: 'JUN 2023',
			data: [
				{ title: 'Amino Tech', value: 150 },
				{ title: 'Imbed', value: 200 },
				{ title: 'Nvision Biomed', value: 150 },
				{ title: 'Axolotl Biologix', value: 100 },
			],
		},
		{
			category: 'JUL 2023',
			data: [
				{ title: 'Amino Tech', value: 150 },
				{ title: 'Imbed', value: 200 },
				{ title: 'Nvision Biomed', value: 150 },
				{ title: 'Axolotl Biologix', value: 100 },
			],
		},
		{
			category: 'AUG 2023',
			data: [
				{ title: 'Amino Tech', value: 200 },
				{ title: 'Imbed', value: 300 },
				{ title: 'Nvision Biomed', value: 250 },
				{ title: 'Axolotl Biologix', value: 200 },
			],
		},
		{
			category: 'SEP 23',
			data: [
				{ title: 'Amino Tech', value: 270 },
				{ title: 'Imbed', value: 450 },
				{ title: 'Nvision Biomed', value: 250 },
				{ title: 'Axolotl Biologix', value: 350 },
			],
		},
		{
			category: 'OCT 23',
			data: [
				{ title: 'Amino Tech', value: 150 },
				{ title: 'Imbed', value: 200 },
				{ title: 'Nvision Biomed', value: 150 },
				{ title: 'Axolotl Biologix', value: 100 },
			],
		},
	];

	const dailyCollectionData = [
		{
			category: 'Medicare',
			data: [
				{ title: 'Current', value: 1000 },
				{ title: 'Previous', value: 5600 },
			],
		},
		{
			category: 'CGS Insurance',
			data: [
				{ title: 'Current', value: 3200 },
				{ title: 'Previous', value: 2300 },
			],
		},
		{
			category: 'AETNA',
			data: [
				{ title: 'Current', value: 4100 },
				{ title: 'Previous', value: 4300 },
			],
		},
		{
			category: 'Conseco',
			data: [
				{ title: 'Current', value: 8600 },
				{ title: 'Previous', value: 7800 },
			],
		},
		{
			category: 'CareSource',
			data: [
				{ title: 'Current', value: 7400 },
				{ title: 'Previous', value: 4700 },
			],
		},
		{
			category: 'Premera Blue Cross',
			data: [
				{ title: 'Current', value: 6200 },
				{ title: 'Previous', value: 8700 },
			],
		},
		{
			category: 'AARP',
			data: [
				{ title: 'Current', value: 4900 },
				{ title: 'Previous', value: 3100 },
			],
		},
		{
			category: 'Amerigroup',
			data: [
				{ title: 'Current', value: 2300 },
				{ title: 'Previous', value: 7200 },
			],
		},
		{
			category: 'Bright Health',
			data: [
				{ title: 'Current', value: 5400 },
				{ title: 'Previous', value: 2600 },
			],
		},
		{
			category: 'Cigna',
			data: [
				{ title: 'Current', value: 8500 },
				{ title: 'Previous', value: 3800 },
			],
		},
		{
			category: 'Geisinger',
			data: [
				{ title: 'Current', value: 4200 },
				{ title: 'Previous', value: 3900 },
			],
		},
		{
			category: 'Health Net',
			data: [
				{ title: 'Current', value: 1200 },
				{ title: 'Previous', value: 1000 },
			],
		},
	];

	const bvChartData = [
		{
			category: 'JAN 2023',
			data: [
				{ title: 'Celera', value: 65 },
				{ title: 'Microlyte', value: 103 },
				{ title: 'Nvision', value: 125 },
				{ title: 'Axolotl', value: 112 },
			],
		},
		{
			category: 'FEB 2023',
			data: [
				{ title: 'Celera', value: 65 },
				{ title: 'Microlyte', value: 103 },
				{ title: 'Nvision', value: 125 },
				{ title: 'Axolotl', value: 112 },
			],
		},
		{
			category: 'MAR 2023',
			data: [
				{ title: 'Celera', value: 65 },
				{ title: 'Microlyte', value: 103 },
				{ title: 'Nvision', value: 125 },
				{ title: 'Axolotl', value: 112 },
			],
		},
		{
			category: 'APR 2023',
			data: [
				{ title: 'Celera', value: 65 },
				{ title: 'Microlyte', value: 103 },
				{ title: 'Nvision', value: 125 },
				{ title: 'Axolotl', value: 112 },
			],
		},
		{
			category: 'MAY 2023',
			data: [
				{ title: 'Celera', value: 65 },
				{ title: 'Microlyte', value: 103 },
				{ title: 'Nvision', value: 125 },
				{ title: 'Axolotl', value: 112 },
			],
		},
		{
			category: 'JUN 2023',
			data: [
				{ title: 'Celera', value: 65 },
				{ title: 'Microlyte', value: 103 },
				{ title: 'Nvision', value: 125 },
				{ title: 'Axolotl', value: 112 },
			],
		},
	];

	// Gradient Bar chart demo data (single-series per category)
	const gradientBarData = [
		{ category: 'Axolot', data: [{ title: 'Paid', value: 180 }] },
		{ category: 'PallinGen', data: [{ title: 'Paid', value: 65 }] },
		{ category: 'Nvision', data: [{ title: 'Paid', value: 180 }] },
		{ category: 'Microlyte', data: [{ title: 'Paid', value: 135 }] },
		{ category: 'Celera', data: [{ title: 'Paid', value: 100 }] },
		{ category: 'Nvision', data: [{ title: 'Paid', value: 135 }] },
		{ category: 'Microlyte', data: [{ title: 'Paid', value: 190 }] },
		{ category: 'PallinGen', data: [{ title: 'Paid', value: 135 }] },
		{ category: 'Axolot', data: [{ title: 'Paid', value: 100 }] },
		{ category: 'Nvision', data: [{ title: 'Paid', value: 190 }] },
		{ category: 'Microlyte', data: [{ title: 'Paid', value: 135 }] },
		{ category: 'Celera', data: [{ title: 'Paid', value: 190 }] },
	];

	const donutChartData = [
		{ title: 'Amino Tech', value: 925 },
		{ title: 'Imbed', value: 2300 },
		{ title: 'Nvision Biomed', value: 2618 },
		{ title: 'Axolotl Biologix', value: 3076 },
		{ title: 'Other', value: 600 },
		{ title: 'Traid Life Sciences', value: 392 },
	];

	const columns: Column[] = [
		{ key: 'name', label: 'Product', width: '150px', type: 'string' },
		{ key: 'category', label: 'Category', width: '250px', type: 'string' },
		{ key: 'price', label: 'Price', width: '200px', type: 'number' },
		{
			key: 'product',
			label: 'Product',
			width: '200px',
			type: 'string',
			hideColumnFilter: true,
		},
		{ key: 'date', label: 'Date', width: '200px', type: 'date' },
		{
			key: 'bvStatus',
			label: 'Status',
			width: '270px',
			type: 'string-status',
			filterOptions: statusesFilterOptions,
		},
	];

	const dataTableItems = useMemo(
		() =>
			Array.from({ length: 50 }).map((_, i) => {
				const id = i + 1;

				const categories = [
					'Medicine',
					'Surgical',
					'Diagnostics',
					'Rehabilitation',
					'Equipment',
					'Consumables',
					'Supplements',
				];

				const products = [
					'Stethoscope',
					'Blood Pressure Monitor',
					'Insulin Pen',
					'Wheelchair',
					'X-Ray Machine',
					'Face Mask',
					'Antibiotic Tablets',
					'Vitamin D Supplement',
				];

				// statuses with fixed colors
				const statuses = [
					{ label: 'Approved', value: 'approved', color: '#117964' },
					{ label: 'Auth Approved', value: 'auth-approved', color: '#26A69A' },
					{
						label: 'Appeal in Process',
						value: 'appeal-in-process',
						color: '#F39C12',
					},
					{ label: 'In Process', value: 'in-process', color: '#1C4B74' },
					{
						label: 'Pending Clinic Response (Missing Data)',
						value: 'pending-clinic-response',
						color: '#E1BA1C',
					},
					{ label: 'Void', value: 'void', color: '#7F8C8D' },
					{ label: 'Denied', value: 'denied', color: '#C0392B' },
					{ label: 'Auth Denied', value: 'auth-denied', color: '#922B21' },
					{ label: 'Order Placed', value: 'order-placed', color: '#34495E' },
					{ label: 'On Hold', value: 'on-hold', color: '#EB9900' },
				];

				// random category/product/status
				const category = categories[i % categories.length];
				const product = products[i % products.length];
				const status = statuses[Math.floor(Math.random() * statuses.length)];

				// random date generator (between 2022-01-01 and 2025-12-31)
				const start = new Date(2022, 0, 1).getTime();
				const end = new Date(2025, 11, 31).getTime();
				const randomDate = new Date(start + Math.random() * (end - start));
				const date = randomDate.toLocaleDateString('en-GB'); // e.g., 06/07/2023

				return {
					id,
					name: `Product ${id}`,
					category,
					product,
					date,
					price: `$${(Math.floor(Math.random() * 900) + 100).toFixed(2)}`, // random price
					status: status.value,
					bvStatus: (
						<Box
							bg={status.color}
							borderRadius={4}
							color="white"
							height="24px"
							px={3}
							display="flex"
							alignItems="center"
							justifyContent="center"
							fontWeight={500}
							fontSize="xs"
						>
							{status.label}
						</Box>
					),
				};
			}),
		[],
	);

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearch(e.target.value);
	};

	const handleClearSearch = () => {
		setSearch('');
	};
	const [paginatedItems, setPaginatedItems] = useState<PaginatedItems[]>([]);
	const [totalCount, setTotalCount] = useState<number>(dataTableItems.length);
	const onFilterApply = ({
		page,
		limit,
		status,
		search = '',
		sort = null,
		columnsFilters = [],
		globalFilters
	}: onFilterApplyProps) => {
		console.log('sort', sort);
		console.log('columnsFilters', {
			page,
			limit,
			status,
			search,
			sort,
			columnsFilters,
			globalFilters
		});
		setLoading(true);
		const start = (page - 1) * limit;
		let paginatedItems = dataTableItems.filter((item) =>
			item.category.includes(search),
		);
		if (status?.value !== '') {
			console.log('hh', status);
			paginatedItems = paginatedItems.filter(
				(items) => items.status === status?.value,
			);
			setTotalCount(paginatedItems.length);
		} else {
			setTotalCount(paginatedItems.length);
		}
		paginatedItems = paginatedItems.slice(start, start + limit);
		setPaginatedItems(paginatedItems);
		setTimeout(() => setLoading(false), 1000);
	};
	const onSelection = (ids: string[]) => {
		console.log('ids', ids);
	};
	const exportAllAsCSV = () => {
		console.log('Exporting all data as CSV');
	};

	const exportAllAsExcel = () => {
		console.log('Exporting all data as Excel');
	};

	const exportAllAsPDF = () => {
		console.log('Exporting all data as PDF');
	};
	return (
		<Box p={4} bg={{ base: '{colors.gray.50}', _dark: '#212121' }}>
			{/* Error Pages Preview Links */}
			<Box
				mb={8}
				ml={{ base: 4, md: 7 }}
				p={6}
				borderWidth="1px"
				borderRadius="lg"
				bg={{ base: 'white', _dark: 'gray.800' }}
			>
				<Text fontSize="xl" fontWeight="bold" mb={4}>
					Error Pages Preview
				</Text>
				<Text fontSize="sm" color="gray.600" mb={4}>
					Click the buttons below to navigate to the error pages
				</Text>
				<Flex gap={3} flexWrap="wrap">
					<FaceliftButton
						size="sm"
						onClick={() => window.open('/error-401', '_blank')}
					>
						401 - Unauthorized
					</FaceliftButton>
					<FaceliftButton
						size="sm"
						onClick={() => window.open('/error-402', '_blank')}
						bg="brand.warning"
						color="brand.textDark"
					>
						402 - Payment Required
					</FaceliftButton>
					<FaceliftButton
						size="sm"
						onClick={() => window.open('/error-403', '_blank')}
						bg="brand.errors"
					>
						403 - Forbidden
					</FaceliftButton>
					<FaceliftButton
						size="sm"
						onClick={() => window.open('/error-404', '_blank')}
						bg="brand.robotGrey"
					>
						404 - Not Found
					</FaceliftButton>
				</Flex>
			</Box>

			{/* ScoreCard examples */}
			<SimpleGrid
				columns={{ base: 1, md: 2, lg: 4 }}
				gap={{ base: 2, md: 2 }}
				mb={8}
				ml={{ base: 0, md: 7 }}
			>
				{scoreCardData.map((card) => (
					<ScoreCard
						key={card.title}
						title={card.title}
						value={card.value}
						subtitle={card.subtitle}
						icon={<BookmarkIcon />}
					/>
				))}
			</SimpleGrid>
			{/* Products Ordered by Units Chart */}
			<Box mb={8} ml={7}>
				<StackedBarChart
					title="Products Ordered by Units (cm)"
					data={stackedChartData}
				/>
			</Box>

			{/* Benefits Verification Grouped Bar Chart & Product Order Analysis Donut Chart */}
			<Stack
				direction={{ base: 'column', lg: 'row' }}
				mb={8}
				ml={{ base: 4, md: 7 }}
				mr={{ base: 4, md: 0 }}
				gap={{ base: 4, lg: 2 }}
			>
				<Box flex="1" minW={{ base: '100%', lg: '666px' }} w="100%">
					<GroupedBarChart title="Benefits Verification" data={bvChartData} />
				</Box>
				<Box flex="1" minW={{ base: '100%', lg: '666px' }} w="100%">
					<DonutChart title="Product Order Analysis" data={donutChartData} />
				</Box>
			</Stack>

			{/* Daily Collection Line Chart */}
			<Box mb={8} ml={7}>
				<LineChart
					title="Collection Chart"
					data={dailyCollectionData}
					isLoading={false}
				/>
			</Box>

			{/* Paid Invoice Amount by Product - Gradient Bar Chart */}
			<Box mb={8} ml={7}>
				<GradientBarChart
					title="Paid Invoice Amount by Product"
					data={gradientBarData}
				/>
			</Box>

			{/* Invoice Aging Summary */}
			<Box mb={6} ml={{ base: 4, lg: 4 }} mr={{ base: 4, lg: 4 }}>
				<InvoiceAgingSummary
					buckets={[
						{ label: '1-30 Days', amount: '$2,000' },
						{ label: '31-60 Days', amount: '$4,000' },
						{ label: '61-90 Days', amount: '$8,000' },
						{ label: '90-120 Days', amount: '$10,000' },
						{ label: '120+ Days', amount: '$12,000' },
					]}
				/>
			</Box>

			{/* Open Invoice Amount by Product with scorecards (grid layout) */}
			<SimpleGrid
				templateColumns={{ base: '1fr', lg: '1fr 450px 450px' }}
				gap={2}
				mb={8}
				ml={{ base: 4, lg: 4 }}
			>
				<Box display="flex" justifyContent="center">
					<Box w="100%">
						<Stack gap={4}>
							<ScoreCard
								title="Open Invoices"
								value={350}
								icon={<DocumentsIcon />}
							/>
							<ScoreCard
								title="Closed Invoices"
								value={100}
								icon={<DocumentsIcon />}
							/>
						</Stack>
					</Box>
				</Box>
				<Box display="flex" justifyContent="center">
					<Box w="100%" maxW="450px">
						<InvoiceChart
							title="Open Invoice Amount by Product"
							data={[
								{ name: 'Axolotl', amount: '$5,860', value: 5860 },
								{ name: 'PalinGen', amount: '$3,000', value: 3000 },
								{ name: 'Nvision', amount: '$2,640', value: 2640 },
								{ name: 'Microlyte', amount: '$2,350', value: 2350 },
								{ name: 'Celera', amount: '$1,050', value: 1050 },
							]}
							isLoading={false}
						/>
					</Box>
				</Box>
				<Box display="flex" justifyContent="center">
					<Box w="100%" maxW="450px">
						<DonutChart
							title="Product Order Analysis"
							data={donutChartData}
							compact
						/>
					</Box>
				</Box>
			</SimpleGrid>

			<DataTable
				tableName="Order Returns"
				loading={loading}
				onSelection={onSelection}
				items={paginatedItems}
				columns={columns}
				topLabel="Order Returns"
				totalCount={totalCount}
				onFilterApply={onFilterApply}
				searchable={true}
				hideShowColumn={true}
				expandable={true}
				paginated={totalCount > 15}
				fullscreen={true}
				exportable={true}
				// globalFilter={{
				// 	title: 'Filters',
				// 	items: [
				// 		{
				// 			name: 'Status',
				// 			options: [
				// 				{
				// 					label: 'Unpaid Invoices',
				// 					id: 'unpaid_Invoices',
				// 				},
				// 				{
				// 					label: 'Due Invoices',
				// 					id: 'due_Invoices',
				// 				},
				// 				{
				// 					label: 'Overdue Invoices',
				// 					id: 'overdue_Invoices',
				// 				},
				// 			],
				// 		},
				// 	],
				// 	clinics: true,
				// }}
				creatableFn={() => {
					console.log('create');
				}}
				statusFilters={[
					{
						label: 'All',
						value: '',
					},
					{
						label: 'Approved',
						value: 'approved',
					},
					{ label: 'Auth Approved', value: 'auth-approved' },
					{
						label: 'Appeal in Process',
						value: 'appeal-in-process',
					},
					{ label: 'In Process', value: 'in-process' },
					{
						label: 'Pending Clinic Response (Missing Data)',
						value: 'pending-clinic-response',
					},
					{ label: 'Void', value: 'void' },
					{ label: 'Denied', value: 'denied' },
					{ label: 'Auth Denied', value: 'auth-denied' },
					{ label: 'Order Placed', value: 'order-placed' },
					{ label: 'On Hold', value: 'on-hold' },
				]}
				actions={[
					{
						label: 'Details',
						icon: <IoEyeOutline />,
						actionFn: () => console.log('hello from action'),
						show: (item: ColumnData) => item.status === 'approved',
					},
					{
						label: 'Expand',
						icon: <LuExpand />,
						actionFn: () => console.log('hello from action'),
					},
				]}
				csv={true}
				excel={true}
				pdf={false}
				exportAllAsCSV={exportAllAsCSV}
				exportAllAsExcel={exportAllAsExcel}
				exportAllAsPDF={exportAllAsPDF}
			/>

			<ColorModeButton />
			<Box mt={4}>
				<DataTable
					tableName="Billing Tools"
					loading={loading}
					onSelection={onSelection}
					items={paginatedItems}
					columns={columns}
					topLabel="Billing Tools"
					isSelectable={true}
					totalCount={dataTableItems.length}
					onFilterApply={onFilterApply}
					bordered={true}
					paginated={totalCount > 15}
				/>
			</Box>
			{/* Date Picker Examples */}
			<Box
				mt={8}
				p={6}
				borderWidth="1px"
				borderRadius="lg"
				bg={{ base: 'white', _dark: 'gray.800' }}
			>
				<Text fontSize="xl" fontWeight="bold" mb={6}>
					Date Picker Examples
				</Text>

				{/* Single Date Picker */}
				<Box mb={6}>
					<Text fontSize="lg" fontWeight="semibold" mb={3}>
						Single Date Picker
					</Text>
					<Box maxW="300px">
						<FaceliftDatePicker
							topLabel="Select a Date"
							value={singleDate}
							onChange={setSingleDate}
							placeholder="Choose date..."
						/>
					</Box>
					<Box mt={2}>
						<Text fontSize="sm" color="gray.600">
							Selected Date: {singleDate || 'None'}
						</Text>
					</Box>
				</Box>

				{/* Date Range Picker */}
				<Box mb={6}>
					<Text fontSize="lg" fontWeight="semibold" mb={3}>
						Date Range Picker
					</Text>
					<Box maxW="400px">
						<FaceliftDatePicker
							isRange={true}
							topLabel="Select Date Range"
							range={dateRange}
							onRangeChange={setDateRange}
							placeholder="Choose date range..."
						/>
					</Box>
					<Box mt={2}>
						<Text fontSize="sm" color="gray.600">
							Start Date: {dateRange.startDate || 'None'}
						</Text>
						<Text fontSize="sm" color="gray.600">
							End Date: {dateRange.endDate || 'None'}
						</Text>
						<Text fontSize="sm" color="gray.600">
							Full Range:{' '}
							{dateRange.startDate && dateRange.endDate
								? `${dateRange.startDate} - ${dateRange.endDate}`
								: 'Incomplete range'}
						</Text>
					</Box>
				</Box>

				{/* Clear Buttons */}
				<Flex gap={3}>
					<FaceliftButton
						size="sm"
						variant="outline"
						onClick={() => setSingleDate('')}
					>
						Clear Single Date
					</FaceliftButton>
					<FaceliftButton
						size="sm"
						variant="outline"
						onClick={() => setDateRange({ startDate: '', endDate: '' })}
					>
						Clear Date Range
					</FaceliftButton>
				</Flex>
			</Box>

			{/* Original Demo Components */}
			<Box mt={8}>
				<Text fontSize="xl" fontWeight="bold" mb={4}>
					Original Components Demo
				</Text>
				<FaceliftButton
					width={'32'}
					startIcon={<span>😀</span>}
					endIcon={<span>🚀</span>}
				>
					Submit
				</FaceliftButton>

				<FaceliftButton
					width={'32'}
					startIcon={<span>😀</span>}
					endIcon={<span>🚀</span>}
					loading
				>
					Submit
				</FaceliftButton>

				<Box width="300px" mt="20px" p={5}>
					<FaceliftInput
						startIcon={<span>😀</span>}
						endIcon={<span>🚀</span>}
						placeholder="Enter text here"
						error="Enter Valid Address"
					/>
					<FaceliftInput
						startIcon={<span>😀</span>}
						placeholder="Enter text here"
					/>
				</Box>
			</Box>
			<Box display="flex" gap={12} flexDirection="column">
				<Box width="300px" mt={12}>
					<FaceliftSelect
						limit={10}
						topLabel="Hello"
						required={true}
						loading={loading}
						onLoadMore={onLoadMore}
						value={singleValue}
						onChange={onSingleChange}
						items={allItems}
					/>
				</Box>
				<Box width="300px" mt={12}>
					<FaceliftSelect
						limit={10}
						loading={loading}
						error="Enter Valid Address"
						isSearchable={false}
						onLoadMore={onLoadMore}
						value={singleValue}
						onChange={onSingleChange}
						items={allItems}
					/>
				</Box>
				<Box width="300px" mt={12}>
					<FaceliftSelect
						limit={10}
						rounded={true}
						topLabel="Hello"
						required={true}
						loading={loading}
						onLoadMore={onLoadMore}
						value={singleValue}
						onChange={onSingleChange}
						items={allItems}
						borderRadius={20}
					/>
				</Box>

				<Box width="300px" mt={12}>
					<FaceliftSelect
						limit={10}
						rounded={true}
						loading={loading}
						error="Enter Valid Address"
						isSearchable={false}
						onLoadMore={onLoadMore}
						value={singleValue}
						onChange={onSingleChange}
						items={allItems}
						borderRadius={20}
					/>
				</Box>
				<Box display="flex" gap={12}>
					<Box width="300px" mt={12}>
						hello here
						<FaceliftSelect
							limit={10}
							loading={loading}
							isMulti={true}
							isSearchable={true}
							onLoadMore={onLoadMore}
							value={multiValue}
							onChange={onMultiChange}
							items={allItems}
							borderRadius={20}
							ai={true}
						/>
					</Box>
					<Box width="300px" mt={12}>
						<FaceliftSelect
							limit={10}
							topLabel="Hello"
							loading={loading}
							isMulti={true}
							error="Enter Valid Address"
							isSearchable={true}
							onLoadMore={onLoadMore}
							value={multiValue}
							onChange={onMultiChange}
							items={allItems}
							borderRadius={20}
						/>
					</Box>
				</Box>
			</Box>
			<ColorModeButton />

			<FaceliftButton
				width={'32'}
				borderRadius="20px"
				startIcon={<span>😀</span>}
				endIcon={<span>🚀</span>}
			>
				Submit
			</FaceliftButton>

			<FaceliftButton
				width={'32'}
				borderRadius="20px"
				startIcon={<span>😀</span>}
				endIcon={<span>🚀</span>}
				sx={{ borderRadius: 4, marginLeft: 2 }}
				loading
			>
				Submit
			</FaceliftButton>

			<Box width="300px" mt="20px" p={5}>
				<FaceliftInput
					startIcon={
						<span>
							<AiOutlineSearch />
						</span>
					}
					endIcon={<span>🚀</span>}
					placeholder="Enter text here"
					error="Enter Valid Address"
				/>
				<FaceliftInput
					startIcon={<span>😀</span>}
					placeholder="Enter text here"
				/>
			</Box>

			{/* Chart Review Form */}
			{/* <Box
				boxShadow="lg"
				direction="column"
				width="full"
				maxWidth="7xl"
				mx="auto"
				p={6}
				bg={{ base: 'white', _dark: 'inherit' }}
			>
				<ChartReviewForm
					data={chartReviewData}
					onChange={handleChartReviewChange}
				/>
			</Box> */}

			{/* Benefit Verification Form */}
			{/* <Box
				boxShadow="lg"
				direction="column"
				width="full"
				maxWidth="7xl"
				mx="auto"
				p={6}
				bg={{ base: 'white', _dark: 'inherit' }}
			>
				<BenefitVerification />
			</Box> */}

			{/* Original Stepper Demo */}
			<Box mt={8} mb={8}>
				<Text fontSize="xl" fontWeight="bold" mb={4}>
					Original Stepper Demo
				</Text>
				<Stepper
					title="Basic Stepper Example"
					steps={stepperSteps}
					onStepChange={(step) => console.log('Demo Step:', step)}
					submitButtonText="Submit Demo"
					onSubmit={() => console.log('Demo Submitted')}
				/>
			</Box>

			<Box mt={8} mb={8}>
				<FaceliftCheckbox
					checked={checked}
					onCheckedChange={(checked: boolean) => setChecked(!!checked)}
					label="Accept terms and conditions"
					size={'xs'}
				/>
			</Box>

			<Box mt={8} mb={8}>
				<Products />
			</Box>
			<Box>
				<FaceliftButton onClick={() => setOpen1(true)}>
					open dialog
				</FaceliftButton>
				<FaceliftDialog
					open={open1}
					onOpenChange={setOpen1}
					showCloseBtn={true}
					title="Title"
					subtitle="Subtitle?"
					footer={
						<>
							<Button onClick={() => setOpen1(false)}>Cancel</Button>
							<Button variant="subtle">Save</Button>
						</>
					}
				>
					<p>HTML Content.</p>
				</FaceliftDialog>
			</Box>
			<Box>
				<FaceliftButton
					onClick={() =>
						setMessages({
							info: '',
							warning: '',
							success: '',
							error: 'Your Profile has not been Updated successfully.',
						})
					}
				>
					open error
				</FaceliftButton>
				<FaceliftMessage
					messageTitle='Something went wrong!'
					message={messages.error}
					error={true}
				/>
				<FaceliftButton
					onClick={() =>
						setMessages({
							info: '',
							warning: '',
							error: '',
							success: 'Your Profile has been Updated successfully.',
						})
					}
				>
					open success
				</FaceliftButton>
				<FaceliftMessage message={messages.success} />
				<FaceliftButton
					onClick={() =>
						setMessages({
							info: '',
							success: '',
							error: '',
							warning: 'This action might affect your data.',
						})
					}
				>
					open warning
				</FaceliftButton>
				<FaceliftMessage
					messageTitle="Warning"
					message={messages.warning}
					customIcon={<TiWarning color={colors.warning} size={64} />}
				/>
				<FaceliftButton
					onClick={() =>
						setMessages({
							success: '',
							error: '',
							warning: '',
							info: 'Congratulations! your Profile is 100% complete',
						})
					}
				>
					open info
				</FaceliftButton>
				<FaceliftMessage
					messageTitle="Just so you know"
					message={messages.info}
					customIcon={<MdInfo color={colors.primary} size={64} />}
				/>
			</Box>

			<Box mt={8} mb={8}>
				<FaceliftSwitch
					checked={switchChecked}
					onCheckedChange={setSwitchChecked}
				/>
			</Box>

			<Box mt={8} mb={8}>
				<Notifications />
			</Box>
			<Box>
				<FaceliftTabs
					value={tab}
					onValueChange={setTab}
					titles={['First Tab', 'Second Tab', 'Third Tab']}
					contents={[
						<Box key="1">First Content</Box>,
						<Box key="2">Second Content</Box>,
						<Box key="3">Third Content</Box>,
					]}
				/>
			</Box>

			<Box mt={4}>
				<General />
			</Box>

			{/* Search Input Demo Section */}
			<Box width="300px" mt="20px" p={5}>
				<FaceLiftSearchInput
					value={search}
					onChange={handleSearchChange}
					onClear={handleClearSearch}
					showClear
					h={'43px'}
				/>
			</Box>

			{/* Drag and Drop Demo Section */}
			<Box>
				<Clinics />
			</Box>
		</Box>
	);
};

export default Test;
